/**
 * กิจกรรมผิดปกติ — ศูนย์จัดการ escalation_queue (ขาดงาน / สาย / ลืมสแกนออก ฯลฯ)
 *
 * เดิมรายการพวกนี้ดูได้อย่างเดียว (ใต้แท็บสรุปเวลา) ไม่มีที่ให้จัดการ — หน้านี้
 * จัดการได้จริง:
 *   - ขาดงานไม่ทราบสาเหตุ → สั่งทับผลได้: "มาทำงานปกติ" (บันทึกสแกนเข้า-ออกแทน
 *     ตามเวลากะ) หรือ "บันทึกเป็นลา" (ใบลาอนุมัติแล้วแทนพนักงาน)
 *   - รายการอื่น → จัดการแล้ว / ปิดเรื่อง (พร้อมเหตุผล)
 *   - เข้างานไม่ครบเวลา (fb#438) → "หักตามเวลาที่หาย" หรือ "ยกเว้น ไม่หัก" (ต้องใส่เหตุผล)
 *     ปิดเรื่อง/ปิดเหมารวม = หักตามเวลาที่หาย
 *   - ปิดเรื่องเหมารวมตามตัวกรอง (เคลียร์รายการสะสมจำนวนมาก)
 * การสั่งทับจะสะท้อนในยอดขาดงาน/เงินเดือนหลัง "รันสรุปเวลาใหม่" — มี banner เตือน
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';
import {
  useEmployees,
  useEscalationList,
  useEscalationSummary,
  useResolveEscalation,
  useResolveAllEscalations,
  useOverrideAttendanceDay,
  type EscalationItem,
} from '../../hooks';
import { ESC_TYPE_LABEL } from '../owner/Reconcile';
import { getLast6Periods, periodLabelShort } from '../../lib/period';
import { AlertTriangle, Check, X, UserCheck, Plane, ArrowRight, Scissors, ShieldCheck } from 'lucide-react';

/** fb#438 — การ์ดเข้างานไม่ครบเวลา (หักค่าจ้างตามนาทีที่หาย เว้นแต่ยกเว้น) */
const SHORTFALL = 'time_shortfall';

const LEAVE_TYPE_OPTIONS = [
  { value: 'sick', label: 'ลาป่วย (ได้ค่าจ้าง)' },
  { value: 'personal', label: 'ลากิจ (ได้ค่าจ้าง)' },
  { value: 'vacation', label: 'พักร้อน (ได้ค่าจ้าง)' },
  { value: 'unpaid', label: 'ลาไม่รับค่าจ้าง (หักเงินวันนั้น)' },
];

const escTypeLabel = (t: string) => ESC_TYPE_LABEL[t] ?? t;
const thDate = (d: string) => new Date(d).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' });

interface EmpOpt { emp_code: string; first_name?: string | null; last_name?: string | null; nickname?: string | null }

export function HREscalations() {
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  // เปิดหน้ามา = เห็นเฉพาะ "ของเดือนนี้" (เลือก "ทุกเดือน" เองได้ถ้าจะย้อนดู).
  // เดิม default เป็นทุกเดือน → ของค้างข้ามเดือนกองรวมกันเป็นหลักร้อย การ์ดของวันนี้
  // ที่ยังแก้ทันเลยจมอยู่ใต้ของเก่าที่ทำอะไรไม่ได้แล้ว
  const [period, setPeriod] = useState<string>(() => getLast6Periods()[0] ?? '');
  const summary = useEscalationSummary(period || undefined);
  const list = useEscalationList(typeFilter, period || undefined);
  const { data: employees } = useEmployees() as { data: EmpOpt[] | undefined };
  const resolve = useResolveEscalation();
  const resolveAll = useResolveAllEscalations();
  const override = useOverrideAttendanceDay();
  const toast = useToast();
  const confirm = useConfirm();

  // modal: บันทึกเป็นลา (เลือกประเภท + หมายเหตุ)
  const [leaveTarget, setLeaveTarget] = useState<EscalationItem | null>(null);
  const [leaveType, setLeaveType] = useState('unpaid');
  const [leaveNote, setLeaveNote] = useState('');
  // modal: ปิดเรื่องเหมารวม (ต้องใส่เหตุผล)
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkNote, setBulkNote] = useState('');
  // modal: ยกเว้น ไม่หัก (fb#438 · ต้องใส่เหตุผล)
  const [waiveTarget, setWaiveTarget] = useState<EscalationItem | null>(null);
  const [waiveNote, setWaiveNote] = useState('');
  // มีการแก้ไปแล้วอย่างน้อย 1 รายการ → เตือนให้รันสรุปเวลาใหม่
  const [touched, setTouched] = useState(false);

  const empName = new Map((employees ?? []).map((e) => [e.emp_code, `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() + (e.nickname ? ` (${e.nickname})` : '')]));
  const nameOf = (code: string) => empName.get(code) || code;

  const items = list.data ?? [];
  const total = summary.data?.total ?? 0;
  const filteredCount = typeFilter
    ? (summary.data?.types.find((t) => t.type === typeFilter)?.count ?? items.length)
    : total;

  async function markPresent(it: EscalationItem) {
    const ok = await confirm({
      title: 'ยืนยันว่ามาทำงานปกติ?',
      message: `${nameOf(it.emp_code)} วันที่ ${thDate(it.date)} — ระบบจะบันทึกเวลาเข้า-ออกแทนตามเวลากะ และนับเป็นวันทำงานปกติ (ไม่หักเงิน)`,
      confirmText: 'มาทำงานปกติ',
    });
    if (!ok) return;
    try {
      await override.mutateAsync({ emp_code: it.emp_code, date: it.date, action: 'present' });
      setTouched(true);
      toast.success('บันทึกแล้ว — นับเป็นวันทำงานปกติ');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  async function submitLeave() {
    if (!leaveTarget) return;
    try {
      await override.mutateAsync({
        emp_code: leaveTarget.emp_code, date: leaveTarget.date,
        action: 'leave', leave_type: leaveType, note: leaveNote.trim() || undefined,
      });
      setTouched(true);
      setLeaveTarget(null);
      setLeaveNote('');
      toast.success('บันทึกเป็นลาแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  async function markResolved(it: EscalationItem) {
    try {
      await resolve.mutateAsync({ esc_id: it.esc_id, emp_code: it.emp_code, date: it.date, resolution: 'resolved' });
      setTouched(true);
      toast.success('ทำเครื่องหมายจัดการแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ไม่สำเร็จ');
    }
  }

  async function markDeduct(it: EscalationItem) {
    try {
      await resolve.mutateAsync({ esc_id: it.esc_id, emp_code: it.emp_code, date: it.date, resolution: 'resolved', decision: 'deduct' });
      setTouched(true);
      toast.success('บันทึกแล้ว — หักค่าจ้างตามเวลาที่หาย');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ไม่สำเร็จ');
    }
  }

  async function submitWaive() {
    if (!waiveTarget) return;
    if (!waiveNote.trim()) { toast.error('ใส่เหตุผลที่ยกเว้นก่อน'); return; }
    try {
      await resolve.mutateAsync({ esc_id: waiveTarget.esc_id, emp_code: waiveTarget.emp_code, date: waiveTarget.date, resolution: 'resolved', decision: 'waive', note: waiveNote.trim() });
      setTouched(true);
      setWaiveTarget(null);
      setWaiveNote('');
      toast.success('ยกเว้นแล้ว — วันนี้ไม่หักค่าจ้าง');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ยกเว้นไม่สำเร็จ');
    }
  }

  async function markIgnored(it: EscalationItem) {
    const ok = await confirm({
      title: 'ปิดเรื่องโดยไม่แก้ไข?',
      message: it.type === SHORTFALL
        ? `${nameOf(it.emp_code)} ${thDate(it.date)} — ปิดเรื่อง = หักค่าจ้างตามเวลาที่หาย และไม่ขึ้นเตือนอีก (ถ้าผู้บริหารอนุญาตให้ใช้ปุ่ม "ยกเว้น ไม่หัก")`
        : `${escTypeLabel(it.type)} · ${nameOf(it.emp_code)} ${thDate(it.date)} — รายการจะถูกปิดและไม่ขึ้นเตือนอีก`,
      confirmText: 'ปิดเรื่อง',
      danger: true,
    });
    if (!ok) return;
    try {
      await resolve.mutateAsync({ esc_id: it.esc_id, emp_code: it.emp_code, date: it.date, resolution: 'ignored', note: 'ปิดเรื่องรายตัวจากหน้ากิจกรรมผิดปกติ' });
      setTouched(true);
      toast.success('ปิดเรื่องแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ไม่สำเร็จ');
    }
  }

  async function submitBulk() {
    if (!bulkNote.trim()) { toast.error('กรุณาใส่เหตุผลการปิดเรื่องทั้งหมด'); return; }
    try {
      const out = await resolveAll.mutateAsync({ type: typeFilter ?? undefined, period: period || undefined, resolution: 'ignored', note: bulkNote.trim() });
      setTouched(true);
      setBulkOpen(false);
      setBulkNote('');
      toast.success(`ปิดเรื่องแล้ว ${out.count.toLocaleString()} รายการ`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ปิดเรื่องไม่สำเร็จ');
    }
  }

  return (
    <AdminLayout title="กิจกรรมผิดปกติ">
      <p className="text-sm text-gray-600 mb-3">
        รายการที่ระบบตรวจพบจากการสรุปเวลา (ขาดงาน / สาย / ลืมสแกนออก ฯลฯ) — แก้ผลได้จากหน้านี้เลย
      </p>

      {touched && (
        <div className="card p-3 mb-4 bg-amber-50 border-amber-200 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800 flex-1">
            แก้ครบแล้วอย่าลืม <strong>รันสรุปเวลาใหม่</strong> เพื่อให้ยอดขาดงาน/เงินเดือนอัปเดตตาม
          </p>
          <Link to="/owner/cycle#reconcile" className="text-sm font-medium text-brand-700 hover:underline shrink-0 flex items-center gap-1">
            ไปรันสรุปเวลา <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {/* ตัวกรองเดือน */}
      <div className="mb-3">
        <label className="label">เดือน</label>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} className="input">
          {getLast6Periods().map((p, i) => (
            <option key={p} value={p}>{periodLabelShort(p)}{i === 0 ? ' (เดือนนี้)' : ''}</option>
          ))}
          <option value="">ทุกเดือน (รวมของค้างเก่า)</option>
        </select>
      </div>

      {/* สรุปตามประเภท = ตัวกรอง */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        <button
          type="button"
          onClick={() => setTypeFilter(null)}
          className={`rounded-full px-3 py-1 text-xs font-medium border ${typeFilter === null ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
        >
          ทั้งหมด {total.toLocaleString()}
        </button>
        {(summary.data?.types ?? []).map((t) => (
          <button
            key={t.type}
            type="button"
            onClick={() => setTypeFilter(typeFilter === t.type ? null : t.type)}
            className={`rounded-full px-3 py-1 text-xs font-medium border ${typeFilter === t.type ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
          >
            {escTypeLabel(t.type)} {t.count.toLocaleString()}
          </button>
        ))}
      </div>

      {filteredCount > 0 && (
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-gray-500">
            {items.length < filteredCount
              ? `แสดง ${items.length.toLocaleString()} จาก ${filteredCount.toLocaleString()} รายการ — จัดการแล้วรายการถัดไปจะขึ้นมาแทน`
              : `${items.length.toLocaleString()} รายการ`}
          </p>
          <button type="button" onClick={() => setBulkOpen(true)} className="text-sm font-medium text-red-600 hover:underline">
            ปิดเรื่องทั้งหมด{typeFilter ? `ประเภทนี้` : ''} ({filteredCount.toLocaleString()})
          </button>
        </div>
      )}

      {list.isLoading || summary.isLoading ? (
        <div className="card p-4 text-sm text-gray-400">กำลังโหลด…</div>
      ) : items.length === 0 ? (
        <div className="card p-6 text-center text-sm text-gray-400">ไม่มีรายการค้าง 🎉</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.esc_id} className="card p-3">
              <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1">
                <div className="min-w-0 flex-1 basis-48">
                  <p className="font-medium text-sm">{nameOf(it.emp_code)}</p>
                  <p className="text-xs text-gray-500">{thDate(it.date)}</p>
                </div>
                <span className="badge-warn shrink-0 whitespace-nowrap">{escTypeLabel(it.type)}</span>
                {/* fb#438 — รายละเอียดเต็มความกว้างการ์ด ไม่ถูกป้ายชนิดยาวบีบบนจอแคบ */}
                {it.detail && <p className="text-xs text-gray-500 w-full">{it.detail}</p>}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {it.type === 'absent_no_evidence' && (
                  <>
                    <button
                      type="button"
                      onClick={() => markPresent(it)}
                      disabled={override.isPending}
                      className="inline-flex items-center gap-1 rounded-lg border border-green-200 bg-green-50 px-2.5 py-1.5 text-xs font-medium text-green-700 hover:bg-green-100 disabled:opacity-50"
                    >
                      <UserCheck className="w-3.5 h-3.5" /> มาทำงานปกติ
                    </button>
                    <button
                      type="button"
                      onClick={() => { setLeaveTarget(it); setLeaveType('unpaid'); }}
                      disabled={override.isPending}
                      className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                    >
                      <Plane className="w-3.5 h-3.5" /> บันทึกเป็นลา
                    </button>
                  </>
                )}
                {it.type === SHORTFALL ? (
                  <>
                    <button
                      type="button"
                      onClick={() => markDeduct(it)}
                      disabled={resolve.isPending}
                      className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                    >
                      <Scissors className="w-3.5 h-3.5" /> หักตามเวลาที่หาย
                    </button>
                    <button
                      type="button"
                      onClick={() => { setWaiveTarget(it); setWaiveNote(''); }}
                      disabled={resolve.isPending}
                      className="inline-flex items-center gap-1 rounded-lg border border-green-200 bg-green-50 px-2.5 py-1.5 text-xs font-medium text-green-700 hover:bg-green-100 disabled:opacity-50"
                    >
                      <ShieldCheck className="w-3.5 h-3.5" /> ยกเว้น ไม่หัก
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => markResolved(it)}
                    disabled={resolve.isPending}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Check className="w-3.5 h-3.5" /> จัดการแล้ว
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => markIgnored(it)}
                  disabled={resolve.isPending}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                >
                  <X className="w-3.5 h-3.5" /> ปิดเรื่อง
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* modal: บันทึกเป็นลา */}
      <Modal open={!!leaveTarget} onClose={() => setLeaveTarget(null)} title="บันทึกเป็นลาแทนพนักงาน">
        {leaveTarget && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              {nameOf(leaveTarget.emp_code)} · วันที่ {thDate(leaveTarget.date)} — ระบบจะสร้างใบลาสถานะอนุมัติแล้วให้เลย
            </p>
            <div>
              <label className="label">ประเภทการลา</label>
              <select className="input" value={leaveType} onChange={(e) => setLeaveType(e.target.value)}>
                {LEAVE_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">หมายเหตุ (ไม่บังคับ)</label>
              <input className="input" value={leaveNote} onChange={(e) => setLeaveNote(e.target.value)} placeholder="เช่น แจ้งลาทางโทรศัพท์" />
            </div>
            <p className="text-[11px] text-gray-400">การบันทึกแทนไม่ตัดโควต้าวันลาอัตโนมัติ — ถ้าต้องการให้นับโควต้า ให้พนักงานยื่นลาย้อนหลังเองแล้วอนุมัติแทน</p>
            <button onClick={submitLeave} disabled={override.isPending} className="btn-primary w-full">
              {override.isPending ? 'กำลังบันทึก…' : 'บันทึกเป็นลา'}
            </button>
          </div>
        )}
      </Modal>

      {/* modal: ยกเว้นเข้างานไม่ครบเวลา (fb#438) */}
      <Modal open={!!waiveTarget} onClose={() => setWaiveTarget(null)} title="ยกเว้น ไม่หักค่าจ้าง">
        {waiveTarget && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              {nameOf(waiveTarget.emp_code)} · วันที่ {thDate(waiveTarget.date)}
              {waiveTarget.detail ? ` — ${waiveTarget.detail}` : ''}
            </p>
            <p className="text-sm text-gray-600">วันนี้จะไม่หักค่าจ้างตามเวลาที่หาย (ยังเก็บนาทีไว้ในประวัติ) · ตัดสินแล้วเปลี่ยนไม่ได้</p>
            <div>
              <label className="label">เหตุผล (บังคับ — บันทึกลงประวัติและพนักงานเห็น)</label>
              <input className="input" value={waiveNote} onChange={(e) => setWaiveNote(e.target.value)} placeholder="เช่น ผู้บริหารสั่งเข้างานช่วงบ่าย หลังทำ OT งานเร่งคืนก่อน" />
            </div>
            <button onClick={submitWaive} disabled={resolve.isPending || !waiveNote.trim()} className="btn-primary w-full">
              {resolve.isPending ? 'กำลังบันทึก…' : 'ยกเว้น ไม่หัก'}
            </button>
          </div>
        )}
      </Modal>

      {/* modal: ปิดเรื่องเหมารวม */}
      <Modal open={bulkOpen} onClose={() => setBulkOpen(false)} title="ปิดเรื่องทั้งหมด">
        <div className="space-y-3">
          {period ? (
            <p className="text-sm text-gray-600">
              จะปิดเฉพาะรายการ{typeFilter ? ` "${escTypeLabel(typeFilter)}"` : ''}ของเดือน <strong>{periodLabelShort(period)}</strong> ({filteredCount.toLocaleString()} รายการ)
              — รายการที่ปิดจะไม่ขึ้นเตือนอีก และไม่เปลี่ยนผลขาดงาน/เงินเดือน (ถ้าต้องการแก้ผล ใช้ปุ่ม "มาทำงานปกติ / บันทึกเป็นลา" รายตัว)
            </p>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                จะปิด{typeFilter ? `รายการ "${escTypeLabel(typeFilter)}"` : 'รายการค้างทุกประเภท'} ทั้งหมด {filteredCount.toLocaleString()} รายการ
                — รายการที่ปิดจะไม่ขึ้นเตือนอีก และไม่เปลี่ยนผลขาดงาน/เงินเดือน (ถ้าต้องการแก้ผล ใช้ปุ่ม "มาทำงานปกติ / บันทึกเป็นลา" รายตัว)
              </p>
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                จะปิดรายการทุกเดือนรวมถึงเดือนปัจจุบัน — ถ้าต้องการปิดเฉพาะเดือนเก่า ให้เลือกเดือนก่อน
              </div>
            </>
          )}
          {(typeFilter === SHORTFALL || (typeFilter === null && (summary.data?.types ?? []).some((t) => t.type === SHORTFALL && t.count > 0))) && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              ใบ "เข้างานไม่ครบเวลา" ทั้งหมดที่ปิดรอบนี้จะถูก<strong>หักค่าจ้างตามเวลาที่หาย</strong> — วันที่ผู้บริหารอนุญาต ให้กด "ยกเว้น ไม่หัก" รายใบก่อนปิดเหมารวม
            </div>
          )}
          <div>
            <label className="label">เหตุผล (บังคับ — บันทึกลงประวัติการใช้งาน)</label>
            <input className="input" value={bulkNote} onChange={(e) => setBulkNote(e.target.value)} placeholder="เช่น รายการสะสมจากข้อมูลทดสอบ" />
          </div>
          <button onClick={submitBulk} disabled={resolveAll.isPending} className="btn-primary w-full !bg-red-600 hover:!bg-red-700">
            {resolveAll.isPending ? 'กำลังปิดเรื่อง…' : `ปิดเรื่องทั้งหมด ${filteredCount.toLocaleString()} รายการ`}
          </button>
        </div>
      </Modal>
    </AdminLayout>
  );
}
